import coreURL from "@ffmpeg/core?url";
import wasmURL from "@ffmpeg/core/wasm?url";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile } from "@ffmpeg/util";

export const enum AudioExtractErrors {
  FFMPEG_LOAD_ERROR = "ffmpegLoadError",
  FFMPEG_WRITE_FILE_ERROR = "ffmpegWriteFileError",
  FFMEG_EXEC_ERROR = "ffmpegExecError",
  FFMPEG_READ_FILE_ERROR = "ffmpegReadFileError",
}

export interface IAudioExtractionResponse {
  success: boolean;
  error?: AudioExtractErrors;
  audioFile?: File;
}

export const extractAudio = async (ffmpeg: FFmpeg, file: File): Promise<IAudioExtractionResponse> => {
  const fileParts = file.name.toLowerCase().split(".");
  const fileName = fileParts.slice(0, fileParts.length - 1).join(".");

  if (!ffmpeg.loaded) {
    await ffmpeg.load({ coreURL, wasmURL });
  }

  const inputFileName = file.name;
  const outputFileName = `${fileName}.wav`;

  // Careful with the speed factor, it should be adjusted based on the audio characteristics.
  const speedFactor = 1.7;

  // Write the input file to ffmpeg's virtual file system
  try {
    await ffmpeg.writeFile(inputFileName, await fetchFile(file));
  } catch (e: any) {
    return {
      success: false,
      error: AudioExtractErrors.FFMPEG_WRITE_FILE_ERROR,
    };
  }

  // Perform the audio extraction using ffmpeg
  try {
    await ffmpeg.exec([
      "-i",
      inputFileName,
      "-filter:a",
      `atempo=${speedFactor}`,
      "-acodec",
      "pcm_s16le",
      "-ar",
      "16000",
      "-ac",
      "1",
      outputFileName,
    ]);
  } catch (e) {
    return {
      success: false,
      error: AudioExtractErrors.FFMPEG_READ_FILE_ERROR,
    };
  }

  // Check if the output file was created and return it if successful
  try {
    const fileData = await ffmpeg.readFile(outputFileName);
    const data = new Uint8Array(fileData as Uint8Array);
    const audioBlob = new Blob([data.buffer], { type: "audio/wav" });
    const outputFile = new File([audioBlob], outputFileName, {
      type: "audio/wav",
    });
    return {
      success: true,
      audioFile: outputFile,
    };
  } catch (e) {
    return {
      success: false,
      error: AudioExtractErrors.FFMPEG_READ_FILE_ERROR,
    };
  }
};
